import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import Layout from '../components/Layout';
import AttendanceGate from '../components/AttendanceGate';
import { useAttendanceGate } from '../hooks/useAttendanceGate';
import GeoLocationCapture from '../components/GeoLocationCapture';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Truck, 
  Plus, 
  Search, 
  Loader2,
  MapPin,
  MapPinOff,
  Phone,
  Edit,
  Trash2,
  X,
  Filter,
  AlertCircle,
  ArrowRightLeft,
  Store
} from 'lucide-react';
import { toast } from 'sonner';
import { useWindowedList } from '../hooks/useWindowedList';
import { cn } from '../lib/utils';

// KEYNOTE: the edit option lives only in the window before approval. Once an
// admin has approved (or rejected) the record it is settled, and only an admin
// can still change it.
const isEditable = (record, isAdmin) =>
  isAdmin || !['approved', 'rejected'].includes(record?.approval_status);

const Distributors = () => {
  const { api, canDelete, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [routes, setRoutes] = useState([]);
  const [filterRoute, setFilterRoute] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all'); // all, with, without
  const [stats, setStats] = useState({ total: 0, with_location: 0, without_location: 0 });
  // Territory handover: move a distributor's whole outlet book to another distributor.
  const [transferSource, setTransferSource] = useState(null);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferRemarks, setTransferRemarks] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [distributorsRes, routesRes] = await Promise.all([
        api.get('/distributors'),
        api.get('/masters/routes')
      ]);
      setDistributors(distributorsRes.data);
      setRoutes(routesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load distributors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/distributors/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this distributor?')) return;
    try {
      await api.delete(`/distributors/${id}`);
      toast.success('Distributor deleted');
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete distributor');
    }
  };

  const closeTransfer = () => {
    setTransferSource(null);
    setTransferTarget('');
    setTransferRemarks('');
  };

  const handleTransfer = async () => {
    if (!transferTarget) {
      toast.error('Select the distributor to transfer to');
      return;
    }
    setTransferring(true);
    try {
      const { data } = await api.post(`/distributors/${transferSource.id}/transfer-outlets`, {
        target_distributor_id: transferTarget,
        remarks: transferRemarks || null
      });
      toast.success(data.message);
      closeTransfer();
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer outlets');
    } finally {
      setTransferring(false);
    }
  };

  const filteredDistributors = distributors.filter(dist => {
    const matchesSearch = dist.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dist.code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRoute = filterRoute === 'all' || dist.route === filterRoute || dist.route_id === filterRoute;
    const matchesLocation = filterLocation === 'all' || 
      (filterLocation === 'with' && dist.geo_location) ||
      (filterLocation === 'without' && !dist.geo_location);
    return matchesSearch && matchesRoute && matchesLocation;
  });

  // Only a screenful is kept in the DOM; the rest is a tap away.
  const windowed = useWindowedList(filteredDistributors);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="distributors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Distributors</h1>
            <p className="text-sm text-slate-500">
              {stats.total} total • 
              <span className="text-emerald-600 ml-1">{stats.with_location} with location</span> • 
              <span className="text-amber-600 ml-1">{stats.without_location} without location</span>
            </p>
          </div>
          <Button
            onClick={() => navigate('/distributors/new')}
            className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white rounded-xl h-11 shadow-lg shadow-sky-200"
            data-testid="add-distributor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Distributor
          </Button>
        </div>

        {/* Location Status Alert */}
        {stats.without_location > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Location Registration Needed</p>
              <p className="text-sm text-amber-600">
                {stats.without_location} distributors don't have location registered. 
                Use the filter below to find them and edit to mark their location.
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search by name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-xl border-slate-200"
              data-testid="search-distributors"
            />
          </div>
          <Select value={filterRoute} onValueChange={setFilterRoute}>
            <SelectTrigger className="w-full sm:w-[180px] h-12 rounded-xl" data-testid="filter-route">
              <SelectValue placeholder="Filter by route" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Routes</SelectItem>
              {routes.map((route) => (
                <SelectItem key={route.id} value={route.name || route.id}>{route.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className={cn(
              "w-full sm:w-[180px] h-12 rounded-xl",
              filterLocation === 'without' && "border-amber-400 bg-amber-50"
            )} data-testid="filter-location">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Location status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Distributors</SelectItem>
              <SelectItem value="with">With Location</SelectItem>
              <SelectItem value="without">Without Location</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Distributors List */}
        <div className="space-y-3">
          {filteredDistributors.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No distributors found</p>
            </div>
          ) : (
            windowed.shown.map((distributor) => (
              <div
                key={distributor.id}
                className={cn(
                  "bg-white rounded-2xl border p-4 hover:shadow-md transition-all card-hover",
                  distributor.geo_location ? "border-slate-100" : "border-amber-200 bg-amber-50/30"
                )}
                data-testid={`distributor-card-${distributor.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      distributor.geo_location ? "bg-sky-100" : "bg-amber-100"
                    )}>
                      <Truck className={cn(
                        "w-5 h-5",
                        distributor.geo_location ? "text-sky-600" : "text-amber-600"
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-slate-900">{distributor.name}</h3>
                        {distributor.approval_status && distributor.approval_status !== 'approved' && (
                          <span className={cn(
                            'px-1.5 py-0.5 rounded text-[10px] font-medium',
                            distributor.approval_status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          )}>
                            {distributor.approval_status.toUpperCase()}
                          </span>
                        )}
                        {!distributor.geo_location && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                            <MapPinOff className="w-3 h-3" />
                            No Location
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        {distributor.code} • Route {distributor.route || distributor.route_id || 'N/A'} • {distributor.zone || 'N/A'}
                      </p>
                      {distributor.distributor_type && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-50 text-sky-700">
                          {distributor.distributor_type}
                        </span>
                      )}
                      {distributor.contacts?.[0]?.phone && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {distributor.contacts[0].phone}
                        </p>
                      )}
                      {distributor.geo_location && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          Location registered
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditable(distributor, isAdmin) ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/distributors/${distributor.id}/edit`)}
                        className="h-8 w-8"
                        data-testid={`edit-distributor-${distributor.id}`}
                      >
                        <Edit className="w-4 h-4 text-slate-500" />
                      </Button>
                    ) : (
                      <span
                        className="text-[10px] font-medium text-slate-400 px-2 py-1 rounded bg-slate-50 whitespace-nowrap"
                        title="Approved distributors can no longer be edited"
                        data-testid={`edit-locked-distributor-${distributor.id}`}
                      >
                        Locked
                      </span>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setTransferSource(distributor)}
                        className="h-8 w-8"
                        title="Transfer all outlets to another distributor"
                        data-testid={`transfer-distributor-${distributor.id}`}
                      >
                        <ArrowRightLeft className="w-4 h-4 text-sky-600" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(distributor.id)}
                        className="h-8 w-8"
                        data-testid={`delete-distributor-${distributor.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

        {windowed.hasMore && (
          <button
            type="button"
            onClick={windowed.showMore}
            className="w-full bg-white rounded-2xl border border-slate-200 p-3 text-sm font-medium text-sky-700 hover:bg-slate-50"
            data-testid="show-more"
          >
            Show more distributors · {windowed.shown.length} of {windowed.total}
          </button>
        )}
        </div>

        {/* Transfer every outlet from one distributor to another (admin only) */}
        {transferSource && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4"
            data-testid="transfer-outlets-dialog"
          >
            <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <ArrowRightLeft className="w-5 h-5 text-sky-600" />
                    Transfer Outlets
                  </h2>
                  <p className="text-sm text-slate-500">
                    Move every outlet under this distributor to another one
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={closeTransfer} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <p className="text-xs font-medium text-slate-500 mb-1">FROM</p>
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  <Store className="w-4 h-4 text-slate-400" />
                  {transferSource.name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{transferSource.code}</p>
              </div>

              <div className="space-y-2">
                <Label>Transfer to *</Label>
                <Select value={transferTarget} onValueChange={setTransferTarget}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="transfer-target">
                    <SelectValue placeholder="Select the receiving distributor" />
                  </SelectTrigger>
                  <SelectContent>
                    {distributors
                      .filter((d) => d.id !== transferSource.id)
                      .map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} ({d.code})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Remarks (optional)</Label>
                <Input
                  value={transferRemarks}
                  onChange={(e) => setTransferRemarks(e.target.value)}
                  placeholder="Why is this book moving?"
                  className="h-12 rounded-xl"
                  data-testid="transfer-remarks"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Every active outlet currently tagged to {transferSource.name} moves across.
                  Outlet codes and approval status stay as they are.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  variant="outline"
                  onClick={closeTransfer}
                  className="flex-1 h-12 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTransfer}
                  disabled={transferring || !transferTarget}
                  className="flex-1 h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="confirm-transfer"
                >
                  {transferring ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                  )}
                  Transfer Outlets
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

// New/Edit Distributor Component
export const DistributorForm = () => {
  const { api, isAdmin } = useAuth();
  const gate = useAttendanceGate();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditMode);
  const [routes, setRoutes] = useState([]);
  const [zones, setZones] = useState([]);
  const [distributorTypes, setDistributorTypes] = useState([]);
  const [cities, setCities] = useState([]);
  const [location, setLocation] = useState(null);
  const [showLocationCapture, setShowLocationCapture] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    distributor_type: '',
    route_id: '',
    route: '',
    zone_id: '',
    zone: '',
    city: '',
    address: '',
    pincode: ''
  });
  const [contacts, setContacts] = useState([{ name: '', phone: '', is_primary: true }]);

  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.name, label: c.name })),
    [cities]
  );

  useEffect(() => {
    fetchRoutes();
    fetchDistributorTypes();
    fetchCities();
    if (isEditMode) {
      fetchDistributor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (formData.route_id) {
      fetchZones(formData.route_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.route_id]);

  const fetchCities = async () => {
    try {
      const response = await api.get('/masters/cities');
      setCities(response.data || []);
    } catch (error) {
      // The city is optional, so a failed master load must not block the form.
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchDistributor = async () => {
    try {
      const response = await api.get(`/distributors/${id}`);
      const dist = response.data;
      setFormData({
        name: dist.name || '',
        code: dist.code || '',
        distributor_type: dist.distributor_type || '',
        route_id: dist.route_id || '',
        route: dist.route || '',
        zone_id: dist.zone_id || '',
        zone: dist.zone || '',
        city: dist.city || '',
        address: dist.address || '',
        pincode: dist.pincode || ''
      });
      if (dist.contacts && dist.contacts.length > 0) {
        setContacts(dist.contacts);
      }
      if (dist.geo_location) {
        setLocation(dist.geo_location);
      }
    } catch (error) {
      console.error('Failed to fetch distributor:', error);
      toast.error('Failed to load distributor');
      navigate('/distributors');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchDistributorTypes = async () => {
    try {
      const response = await api.get('/masters/distributor-types');
      setDistributorTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch distributor types:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/masters/routes');
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  const fetchZones = async (routeId) => {
    try {
      const response = await api.get(`/masters/zones?route_id=${routeId}`);
      setZones(response.data);
    } catch (error) {
      console.error('Failed to fetch zones:', error);
    }
  };

  const addContact = () => {
    setContacts([...contacts, { name: '', phone: '', is_primary: false }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter distributor name');
      return;
    }

    // Required: it decides how the customer is reported on, and the API rejects
    // a blank one, so catch it here rather than after a round trip.
    if (!formData.distributor_type) {
      toast.error('Please select the distributor type');
      return;
    }

    if (!formData.city) {
      toast.error('Please select the city');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        geo_location: location ? { lat: location.lat, lng: location.lng } : null,
        contacts
      };
      // New distributors get their code generated server-side; only an admin
      // editing an existing one may send a code of their own.
      if (!isEditMode || !isAdmin) {
        delete payload.code;
      }
      
      if (isEditMode) {
        await api.put(`/distributors/${id}`, payload);
        toast.success('Distributor updated successfully!');
      } else {
        if (!location) {
          toast.error('Please capture location');
          setLoading(false);
          return;
        }
        await api.post('/distributors', payload);
        toast.success('Distributor created successfully!');
      }
      navigate('/distributors');
    } catch (error) {
      console.error('Failed to save distributor:', error);
      toast.error(isEditMode ? 'Failed to update distributor' : 'Failed to create distributor');
    } finally {
      setLoading(false);
    }
  };

  // Handle location update for existing distributor without location
  const handleLocationUpdate = async () => {
    if (!location) {
      toast.error('Please capture location first');
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/distributors/${id}/location`, { lat: location.lat, lng: location.lng });
      toast.success('Location registered successfully!');
      setShowLocationCapture(false);
    } catch (error) {
      console.error('Failed to update location:', error);
      toast.error('Failed to register location');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  if (!isEditMode && !gate.allowed) {
    return (
      <Layout>
        <div className="py-10">
          <AttendanceGate
            reason={gate.reason}
            punchOutTime={gate.punchOutTime}
            action="a new customer/distributor"
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="distributor-form-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isEditMode ? 'Edit Distributor' : 'Add Distributor'}</h1>
          <p className="text-sm text-slate-500">{isEditMode ? 'Update distributor details' : 'Create a new distributor'}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* GPS Location Section */}
          {isEditMode && !location ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <MapPinOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Location Not Registered</p>
                  <p className="text-sm text-amber-600 mb-3">
                    This distributor doesn't have a location registered yet. Click below to capture and register the location.
                  </p>
                  {showLocationCapture ? (
                    <div className="space-y-3">
                      <GeoLocationCapture 
                        onLocationCapture={setLocation} 
                        required={false}
                        initialLocation={location}
                      />
                      {location && (
                        <Button
                          type="button"
                          onClick={handleLocationUpdate}
                          disabled={loading}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                        >
                          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                          Register This Location
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setShowLocationCapture(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Register Location Now
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <GeoLocationCapture 
              onLocationCapture={setLocation} 
              required={!isEditMode}
              initialLocation={location}
            />
          )}

          {/* Distributor Code - editable by an admin, generated on creation */}
          {isEditMode && (
            <div className="space-y-2">
              <Label>Distributor Code {isAdmin ? '*' : ''}</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="e.g. DIST00042"
                className="h-12 rounded-xl"
                disabled={!isAdmin}
                data-testid="distributor-code"
              />
              <p className="text-xs text-slate-500">
                {isAdmin
                  ? 'Must be unique. Changing it does not affect the outlets tagged under this distributor.'
                  : 'Only an admin can change the distributor code.'}
              </p>
            </div>
          )}

          {/* Distributor Name */}
          <div className="space-y-2">
            <Label>Distributor Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter distributor name"
              className="h-12 rounded-xl"
              required
              data-testid="distributor-name"
            />
          </div>

          {/* Distributor Type - master driven (Masters > Distributor Types) */}
          <div className="space-y-2">
            <Label>Distributor Type *</Label>
            <Select
              value={formData.distributor_type}
              onValueChange={(value) => setFormData({ ...formData, distributor_type: value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="distributor-type">
                <SelectValue placeholder="Select distributor type" />
              </SelectTrigger>
              <SelectContent>
                {distributorTypes.map((t) => (
                  <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {distributorTypes.length === 0 && (
              <p className="text-xs text-amber-600">
                No distributor types configured — add them under Masters &gt; Distributor Types.
              </p>
            )}
          </div>

          {/* Route */}
          <div className="space-y-2">
            <Label>Route</Label>
            <Select
              value={formData.route_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, route_id: value === "none" ? "" : value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="route-select">
                <SelectValue placeholder="Select route" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select Route</SelectItem>
                {routes.map((route) => (
                  <SelectItem key={route.id} value={route.id}>{route.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Also show route from imported data if available */}
            {formData.route && !formData.route_id && (
              <p className="text-sm text-slate-500">Imported Route: {formData.route}</p>
            )}
          </div>

          {/* Zone */}
          <div className="space-y-2">
            <Label>Zone</Label>
            <Select
              value={formData.zone_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, zone_id: value === "none" ? "" : value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="zone-select">
                <SelectValue placeholder="Select zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select Zone</SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Also show zone from imported data if available */}
            {formData.zone && !formData.zone_id && (
              <p className="text-sm text-slate-500">Imported Zone: {formData.zone}</p>
            )}
          </div>

          {/* City - the same admin-managed master the outlet form uses. Optional
              here: it names the place on the TADA statement, and several hundred
              customers predate the field. */}
          <div className="space-y-2">
            <Label>City *</Label>
            <SearchableSelect
              testId="distributor-city"
              value={formData.city}
              onChange={(value) => setFormData({ ...formData, city: value })}
              options={cityOptions}
              placeholder="Select city"
              searchPlaceholder="Type a city name…"
              emptyMessage="No city matches that"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter address"
              className="h-12 rounded-xl"
              data-testid="address"
            />
          </div>

          {/* Pincode */}
          <div className="space-y-2">
            <Label>Pincode</Label>
            <Input
              value={formData.pincode}
              onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              placeholder="Enter pincode"
              className="h-12 rounded-xl"
              data-testid="pincode"
            />
          </div>

          {/* Contacts */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Contacts</Label>
              <Button type="button" variant="outline" size="sm" onClick={addContact} className="rounded-xl">
                <Plus className="w-4 h-4 mr-1" /> Add Contact
              </Button>
            </div>
            {contacts.map((contact, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input
                  placeholder="Name"
                  value={contact.name}
                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                  className="flex-1 h-10 rounded-xl"
                />
                <Input
                  placeholder="Phone"
                  value={contact.phone}
                  onChange={(e) => updateContact(index, 'phone', e.target.value)}
                  className="flex-1 h-10 rounded-xl"
                />
                {contacts.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(index)}>
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/distributors')}
              className="flex-1 h-12 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
              data-testid="submit-distributor"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Distributor' : 'Create Distributor'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Distributors;
